import { useEffect, useState } from 'react';

import BackButton from '@perpay-web/components/base/BackButton/BackButton';
import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import { useToastContext } from '@perpay-web/components/base/ToastProvider/useToastContext';
import WarningLoaderCircle from '@perpay-web/components/base/WarningLoaderCircle/WarningLoaderCircle';
import FocusContent from '@perpay-web/components/composite/FocusLayout/FocusContent';
import FocusFooter from '@perpay-web/components/composite/FocusLayout/FocusFooter';
import FocusHeader from '@perpay-web/components/composite/FocusLayout/FocusHeader';
import FocusLayout from '@perpay-web/components/composite/FocusLayout/FocusLayout';
import Stack from '@perpay-web/components/design-system/Stack/Stack';
import CardCta from '@perpay-web/fintech/components/base/CardCta/CardCta';
import { AddBankAccountButton } from '@perpay-web/fintech/components/composite/AddBankAccountButton/AddBankAccountButton';
import RadioBankAccountDisplay from '@perpay-web/fintech/components/composite/BankAccountDisplay/RadioBankAccountDisplay';
import { useLoadBankAccounts } from '@perpay-web/fintech/hooks/useLoadBankAccounts';
import { BankAccount } from '@perpay-web/fintech/reducers/entities/banks';
import { firstItemOf } from '@perpay-web/utils/arrayUtils';
import { getClassName } from '@perpay-web/utils/domUtils';

import styles from './ChooseBankBase.scss';

type ChooseBankProps = {
    onBack?: VoidFunction;
    onSuccess: (bankAccount: BankAccount | undefined) => void;
    buttonTheme?: 'card';
    skipMFA?: boolean;
};

export const ChooseBankBase = ({
    onBack = undefined,
    onSuccess,
    buttonTheme = undefined,
    skipMFA = undefined,
}: ChooseBankProps) => {
    const { bankAccounts, isLoading } = useLoadBankAccounts();
    const { addToast } = useToastContext();
    const [selectedBankAccountUuid, setSelectedBankAccountUuid] = useState<
        string | null
    >(null);

    useEffect(() => {
        if (bankAccounts.length > 0 && !selectedBankAccountUuid) {
            const primaryBA =
                bankAccounts.find((ba) => ba.isPrimary) ||
                firstItemOf(bankAccounts);
            setSelectedBankAccountUuid(primaryBA.uuid);
        }
    }, [bankAccounts, selectedBankAccountUuid]);

    const handleSubmit = () => {
        onSuccess(
            bankAccounts.find(
                (bankAccount) => bankAccount.uuid === selectedBankAccountUuid,
            ),
        );
    };

    if (isLoading) {
        return <WarningLoaderCircle />;
    }

    return (
        <FocusLayout className={styles['focus-layout']}>
            <FocusHeader
                className={styles['focus-header']}
                leftButtonElement={<BackButton onClick={onBack} />}
                title='Choose Bank'
            />

            <FocusContent className={styles['focus-content']}>
                <Stack className={getClassName(styles['inner-stack'])}>
                    {bankAccounts.map((bankAccount) => (
                        <RadioBankAccountDisplay
                            key={bankAccount.uuid}
                            bankAccount={bankAccount}
                            isSelected={
                                selectedBankAccountUuid === bankAccount.uuid
                            }
                            onSelect={() =>
                                setSelectedBankAccountUuid(bankAccount.uuid)
                            }
                        />
                    ))}
                </Stack>
                <Stack
                    className={getClassName(
                        styles['inner-stack'],
                        styles['larger-gap'],
                    )}
                >
                    <AddBankAccountButton
                        onSuccess={(bank: BankAccount) => {
                            onSuccess(bank);
                        }}
                        onFailure={() => {
                            addToast({
                                message:
                                    'Unable to add bank account.  Please check your bank account details and try again, or use a different bank account.',
                                type: 'error',
                            });
                        }}
                        buttonTheme={buttonTheme}
                        skipMFA={skipMFA}
                    />
                </Stack>
            </FocusContent>
            <FocusFooter>
                {buttonTheme === 'card' ? (
                    <CardCta
                        Component={CustomButton}
                        mode='dark'
                        type='submit'
                        onClick={handleSubmit}
                        disabled={!selectedBankAccountUuid}
                    >
                        Save Changes
                    </CardCta>
                ) : (
                    <CustomButton
                        action='primary'
                        disabled={!selectedBankAccountUuid}
                        onClick={handleSubmit}
                    >
                        Save Changes
                    </CustomButton>
                )}
            </FocusFooter>
        </FocusLayout>
    );
};
